import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  TextInput,
  Alert,
  Dimensions,
  FlatList,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');

// ─── JobEng DB connection (epr-compliance-routes.js on the server) ───────────
const JE_API_BASE = 'https://api.jobeng.ai';
const JE_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const jeHeaders = async () => {
  const token = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('userToken')) || '';
  return {
    'Content-Type': 'application/json',
    'x-api-key': JE_API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
const jeCidNum = async () => {
  const raw = (await AsyncStorage.getItem('company_id')) || '0';
  return parseInt(raw, 10) || 0;
};
const jeNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

// Map a DB company_products row → this screen's product card shape.
const jeMapProduct = (r) => ({
  id: String(r.id),
  name: r.name || 'Untitled',
  category: r.category || 'general',
  description: r.description || '',
  price: jeNum(r.price),
  eprFee: r.price != null ? `£${jeNum(r.price).toFixed(2)}` : '£0.00',
  sku: r.sku || '',
  stock: jeNum(r.stock_quantity),
  unit: r.unit || '',
  status: r.status || 'active',
  // Derived display fields the card expects (DB has no weight/recyclability columns)
  weight: r.unit && /kg|g/i.test(r.unit) ? `${jeNum(r.stock_quantity)}${r.unit}` : '—',
  recyclability: '—',
  carbonFootprint: '—',
  _raw: r,
});

const EnvironmentalProductResponsibilityScreen = ({ navigation }) => {
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [eprData, setEprData] = useState({});
  const [products, setProducts] = useState([]);
  const [complianceStatus, setComplianceStatus] = useState([]);
  const [reports, setReports] = useState([]);
  const [targets, setTargets] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('EPRScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  
  // CRM & AI States
  const [showAICopilot, setShowAICopilot] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [funnelData, setFunnelData] = useState([]);
  const [customerInteractions, setCustomerInteractions] = useState([]);
  const [nextBestOffers, setNextBestOffers] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState({});
  const [customers, setCustomers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  
  // Modal States
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  
  // Form States
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'electronics',
    weight: '',
    recyclability: '',
    carbonFootprint: '',
    eprFee: '',
    status: 'pending'
  });
  
  const [newOpportunity, setNewOpportunity] = useState({
    name: '',
    customerId: '',
    stage: 'prospecting',
    value: '',
    probability: 50,
    expectedCloseDate: new Date(),
  });
  
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    location: '',
    industry: '',
  });

  const [newInteraction, setNewInteraction] = useState({
    type: 'call',
    title: '',
    description: '',
    duration: '30',
    notes: '',
  });

  // FlatList ref + section index map for "View All" scroll-to-section.
  const flatListRef = useRef(null);
  const sectionIndex = { overview: 0, 'quick-actions': 1, categories: 2, products: 3, opportunity: 4, metrics: 5, activities: 6, deadlines: 7 };
  const scrollToSection = (key) => {
    const idx = sectionIndex[key];
    if (idx != null && flatListRef.current) {
      try { flatListRef.current.scrollToIndex({ index: idx, animated: true }); } catch {}
    }
  };

  // "View All" modals for activities & deadlines (reliable full-list view).
  const [showAllActivitiesModal, setShowAllActivitiesModal] = useState(false);
  const [showAllDeadlinesModal, setShowAllDeadlinesModal] = useState(false);
  
  // EPR Categories (simplified)
  const eprCategories = useMemo(() => [
    {
      id: 'electronics',
      name: 'Electronics & WEEE',
      icon: 'laptop',
      color: '#3B82F6',
      description: 'Waste Electrical and Electronic Equipment',
      kpis: [
        { label: 'Collection Rate', value: '65%', change: '+5.2%', trend: 'up' },
        { label: 'Compliance Score', value: '92%', change: '+3.1%', trend: 'up' },
      ]
    },
    {
      id: 'packaging',
      name: 'Packaging Materials',
      icon: 'package-variant',
      color: '#10B981',
      description: 'Packaging waste and materials responsibility',
      kpis: [
        { label: 'Recycling Rate', value: '70%', change: '+8.2%', trend: 'up' },
        { label: 'Cost Savings', value: '£8.5K', change: '+12%', trend: 'up' },
      ]
    },
    {
      id: 'batteries',
      name: 'Batteries',
      icon: 'battery',
      color: '#F59E0B',
      description: 'Battery collection and recycling obligations',
      kpis: [
        { label: 'Collection', value: '45%', change: '+3.5%', trend: 'up' },
        { label: 'Compliance', value: '88%', change: '+2.2%', trend: 'up' },
      ]
    },
    {
      id: 'vehicles',
      name: 'End-of-Life Vehicles',
      icon: 'car',
      color: '#8B5CF6',
      description: 'Vehicle recycling and recovery requirements',
      kpis: [
        { label: 'Recovery Rate', value: '95%', change: '+1.8%', trend: 'up' },
        { label: 'Reuse', value: '85%', change: '+4.2%', trend: 'up' },
      ]
    },
    {
      id: 'textiles',
      name: 'Textiles',
      icon: 'tshirt-crew',
      color: '#EF4444',
      description: 'Textile waste and circular economy',
      kpis: [
        { label: 'Collection', value: '55%', change: '+6.1%', trend: 'up' },
        { label: 'Recycling', value: '40%', change: '+3.2%', trend: 'up' },
      ]
    },
    {
      id: 'furniture',
      name: 'Furniture',
      icon: 'sofa',
      color: '#06B6D4',
      description: 'Furniture lifecycle and material responsibility',
      kpis: [
        { label: 'Recyclability', value: '60%', change: '+5.5%', trend: 'up' },
        { label: 'Durability', value: '90%', change: '+2.3%', trend: 'up' },
      ]
    }
  ], []);

  // Quick Actions
  const quickActions = useMemo(() => [
    {
      id: 'add_product',
      title: 'Add Product',
      icon: 'package-variant-plus',
      color: '#10B981',
      action: () => setShowProductModal(true)
    },
    {
      id: 'log_interaction',
      title: 'Log Interaction',
      icon: 'message-text',
      color: '#3B82F6',
      action: () => setShowInteractionModal(true)
    },
    {
      id: 'add_opportunity',
      title: 'Add Opportunity',
      icon: 'briefcase-plus',
      color: '#F59E0B',
      action: () => setShowOpportunityModal(true)
    },
    {
      id: 'add_customer',
      title: 'Add Customer',
      icon: 'account-plus',
      color: '#8B5CF6',
      action: () => setShowCustomerModal(true)
    },
    {
      id: 'export_data',
      title: 'Export Data',
      icon: 'export',
      color: '#06B6D4',
      action: () => setShowExportModal(true)
    },
    {
      id: 'ai_assistant',
      title: 'AI Assistant',
      icon: 'robot',
      color: '#EC4899',
      action: () => setShowAIModal(true)
    }
  ], []);

  // Sample data (simplified)
  const sampleDashboardData = useMemo(() => ({
    overview: {
      totalProducts: 156,
      complianceScore: 78,
      activeOpportunities: 8,
      totalCustomers: 45,
      annualEPRCost: '£58K',
      savingsAchieved: '£12K',
    },
    recentActivities: [
      {
        id: 'act_001',
        type: 'compliance',
        title: 'WEEE Report Submitted',
        description: 'Q4 2023 compliance report submitted to EA',
        timestamp: '2 hours ago',
        icon: 'file-document',
        color: '#3B82F6'
      },
      {
        id: 'act_002',
        type: 'customer',
        title: 'New Opportunity Created',
        description: 'EPR Package for Alpha Business - £25K',
        timestamp: '4 hours ago',
        icon: 'briefcase-plus',
        color: '#F59E0B'
      },
      {
        id: 'act_003',
        type: 'product',
        title: 'Product Added',
        description: 'Smartphone Pro X added to EPR registry',
        timestamp: '6 hours ago',
        icon: 'package-variant',
        color: '#10B981'
      },
      {
        id: 'act_004',
        type: 'compliance',
        title: 'Deadline Reminder',
        description: 'Packaging data submission due in 7 days',
        timestamp: '8 hours ago',
        icon: 'calendar-alert',
        color: '#EF4444'
      },
    ],
    upcomingDeadlines: [
      {
        id: 'deadline_001',
        title: 'Packaging Data Submission',
        dueDate: '2024-03-31',
        priority: 'high',
        category: 'packaging'
      },
      {
        id: 'deadline_002',
        title: 'Quarterly Carbon Report',
        dueDate: '2024-04-15',
        priority: 'medium',
        category: 'general'
      },
      {
        id: 'deadline_003',
        title: 'Battery Compliance Audit',
        dueDate: '2024-05-01',
        priority: 'medium',
        category: 'batteries'
      },
    ],
    environmentalMetrics: [
      {
        id: 'metric_001',
        label: 'Carbon Footprint',
        value: '2,450',
        unit: 'tonnes CO₂e',
        progress: 75,
        trend: 'down',
        change: -8.5
      },
      {
        id: 'metric_002',
        label: 'Recycling Rate',
        value: '78',
        unit: '%',
        progress: 82,
        trend: 'up',
        change: +5.2
      },
      {
        id: 'metric_003',
        label: 'Waste Generated',
        value: '156',
        unit: 'tonnes',
        progress: 70,
        trend: 'down',
        change: -12.3
      },
      {
        id: 'metric_004',
        label: 'Energy Consumption',
        value: '1,250',
        unit: 'MWh',
        progress: 65,
        trend: 'down',
        change: -6.8
      },
    ]
  }), []);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          loadDashboardData(),
          loadProducts(),
          loadCompliance(),
          loadCustomers(),
          loadOpportunities(),
          initializeAICopilot()
        ]);
      } catch (error) {
        console.error('Error initializing data:', error);
        Alert.alert('Initialization Error', 'Failed to load EPR data');
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeData();
  }, []);

  const getCid = async () => await AsyncStorage.getItem('company_id') || '0';

  const loadDashboardData = useCallback(async () => {
    const cid = await getCid();
    try {
      const savedData = await AsyncStorage.getItem('epr_dashboard_data_' + (await getCid()));
      if (savedData) {
        setEprData(JSON.parse(savedData));
      } else {
        // Empty state for new companies
        setEprData({ overview: { totalProducts: 0, complianceScore: 0, activeOpportunities: 0, totalCustomers: 0, annualEPRCost: '£0', savingsAchieved: '£0' }, recentActivities: [] });
        await AsyncStorage.setItem('epr_dashboard_data_' + (await getCid()), JSON.stringify(sampleDashboardData));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setEprData({ overview: { totalProducts: 0, complianceScore: 0, activeOpportunities: 0, totalCustomers: 0, annualEPRCost: '£0', savingsAchieved: '£0' }, recentActivities: [] });
    }
  }, [sampleDashboardData]);

  const loadProducts = useCallback(async () => {
    try {
      // 1) Live DB first
      const companyId = await jeCidNum();
      const headers = await jeHeaders();
      const res = await fetch(`${JE_API_BASE}/api/epr/products?company_id=${companyId}`, { headers });
      if (res.ok) {
        const json = await res.json().catch(() => null);
        if (json?.success && Array.isArray(json.data)) {
          const mapped = json.data.map(jeMapProduct);
          setProducts(mapped);
          try { await AsyncStorage.setItem('epr_products_db_' + companyId, JSON.stringify(mapped)); } catch {}
          return;
        }
      }
      // 2) Cached DB copy
      const cached = await AsyncStorage.getItem('epr_products_db_' + companyId);
      if (cached) { setProducts(JSON.parse(cached)); return; }
      // 3) Empty
      setProducts([]);
    } catch (error) {
      console.warn('[loadProducts]', error.message);
      try {
        const companyId = await jeCidNum();
        const cached = await AsyncStorage.getItem('epr_products_db_' + companyId);
        if (cached) { setProducts(JSON.parse(cached)); return; }
      } catch {}
      setProducts([]);
    }
  }, []);

  const loadCompliance = useCallback(async () => {
    try {
      const companyId = await jeCidNum();
      const headers = await jeHeaders();
      const res = await fetch(`${JE_API_BASE}/api/epr/overview?company_id=${companyId}`, { headers });
      if (res.ok) {
        const json = await res.json().catch(() => null);
        if (json?.success && json.data) {
          const audits = Array.isArray(json.data.audits) ? json.data.audits : [];
          const docs   = Array.isArray(json.data.documents) ? json.data.documents : [];
          // Compliance status cards from audits
          setComplianceStatus(audits.map(a => ({
            id: String(a.id),
            name: a.audit_name,
            type: a.audit_type,
            status: a.status,
            score: a.compliance_score != null ? jeNum(a.compliance_score) : null,
            date: a.audit_date,
            nextDate: a.next_audit_date,
            findings: a.findings,
            recommendations: a.recommendations,
          })));
          // Reports list from compliance documents
          setReports(docs.map(d => ({
            id: String(d.id),
            name: d.document_name,
            type: d.document_type,
            status: d.compliance_status,
            effectiveDate: d.effective_date,
            expiryDate: d.expiry_date,
            regulatoryBody: d.regulatory_body,
          })));
          try {
            await AsyncStorage.setItem('epr_compliance_db_' + companyId,
              JSON.stringify({ audits, docs }));
          } catch {}
          return;
        }
      }
      const cached = await AsyncStorage.getItem('epr_compliance_db_' + companyId);
      if (cached) {
        const { audits = [], docs = [] } = JSON.parse(cached);
        setComplianceStatus(audits.map(a => ({ id: String(a.id), name: a.audit_name, status: a.status, score: a.compliance_score })));
        setReports(docs.map(d => ({ id: String(d.id), name: d.document_name, status: d.compliance_status })));
      }
    } catch (error) {
      console.warn('[loadCompliance]', error.message);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const sampleCustomers = [
        {
          id: 'cust_001',
          name: 'Olivia Johnson',
          company: 'Alpha Business',
          email: 'olivia@alphabusiness.com',
          phone: '+1 234 567 8900',
          location: 'Toronto, Canada',
          industry: 'Technology'
        },
        {
          id: 'cust_002',
          name: 'Michael Chen',
          company: 'Beta Corp',
          email: 'michael@betacorp.com',
          phone: '+1 234 567 8901',
          location: 'Vancouver, Canada',
          industry: 'Manufacturing'
        }
      ];
      setCustomers([]);
      await AsyncStorage.setItem('epr_customers_' + (await getCid()), JSON.stringify(sampleCustomers));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, []);

  const loadOpportunities = useCallback(async () => {
    try {
      const sampleOpportunities = [
        {
          id: 'opp_001',
          name: 'EPR Compliance Package',
          customerId: 'cust_001',
          customerName: 'Olivia Johnson',
          company: 'Alpha Business',
          stage: 'negotiation',
          value: 25000,
          probability: 75,
          expectedCloseDate: '2024-02-28',
        }
      ];
      setOpportunities([]);
      setSelectedOpportunity(null);
      await AsyncStorage.setItem('epr_opportunities_' + (await getCid()), JSON.stringify(sampleOpportunities));
    } catch (error) {
      console.error('Error loading opportunities:', error);
    }
  }, []);

  // AI Copilot Functions
  const initializeAICopilot = () => {
    const initialMessages = [
      {
        id: 'msg_001',
        type: 'ai',
        content: "Hello, I'm your EPR AI assistant. I can help you with compliance questions, sales opportunities, and customer insights.",
        timestamp: new Date().toISOString()
      }
    ];
    setAiMessages(initialMessages);
  };

  const handleSendToAI = async () => {
    if (!aiInput.trim()) return;

    const userMessage = {
      id: `msg_${Date.now()}`,
      type: 'user',
      content: aiInput,
      timestamp: new Date().toISOString()
    };
    
    setAiMessages(prev => [...prev, userMessage]);
    setAiInput('');
    setIsAiTyping(true);

    setTimeout(() => {
      const response = {
        id: `msg_${Date.now()}_ai`,
        type: 'ai',
        content: `I understand you're asking about: "${aiInput}". I can help you with:\n\n• EPR Compliance Questions\n• Customer Insights\n• Deal Probability Analysis\n• Environmental Metrics\n\nWhat specific area would you like more details on?`,
        timestamp: new Date().toISOString()
      };
      
      setAiMessages(prev => [...prev, response]);
      setIsAiTyping(false);
    }, 1500);
  };

  // Data Operations
  const addProduct = async () => {
    try {
      const companyId = await jeCidNum();
      const headers = await jeHeaders();

      // Persist to DB. Map the EPR form fields to company_products columns.
      let saved = null;
      try {
        const res = await fetch(`${JE_API_BASE}/api/epr/products`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            company_id: companyId,
            name: newProduct.name,
            category: newProduct.category,
            price: parseFloat(String(newProduct.eprFee).replace(/[^0-9.]/g, '')) || 0,
            unit: newProduct.weight || null,
            status: newProduct.status === 'compliant' ? 'active' : (newProduct.status || 'active'),
            description: `Recyclability: ${newProduct.recyclability || '—'}, Carbon: ${newProduct.carbonFootprint || '—'}`,
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success) saved = json.data;
      } catch (e) { console.warn('[addProduct] DB save failed:', e.message); }

      const product = saved
        ? jeMapProduct(saved)
        : { id: `prod_${Date.now()}`, ...newProduct, registrationDate: new Date().toISOString().split('T')[0] };

      const updatedProducts = [product, ...products];
      setProducts(updatedProducts);
      try { await AsyncStorage.setItem('epr_products_db_' + companyId, JSON.stringify(updatedProducts)); } catch {}
      
      setShowProductModal(false);
      setNewProduct({
        name: '',
        category: 'electronics',
        weight: '',
        recyclability: '',
        carbonFootprint: '',
        eprFee: '',
        status: 'pending'
      });
      
      Alert.alert('Success', 'Product added successfully');
    } catch (error) {
      console.error('Error adding product:', error);
      Alert.alert('Error', 'Failed to add product');
    }
  };

  const addCustomer = async () => {
    try {
      if (!newCustomer.name?.trim()) {
        Alert.alert('Required', 'Please enter a customer name');
        return;
      }
      const companyId = await jeCidNum();
      const headers = await jeHeaders();

      let saved = null;
      try {
        const res = await fetch(`${JE_API_BASE}/api/epr/crm/customers`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            company_id: companyId,
            name: newCustomer.name,
            email: newCustomer.email || null,
            phone: newCustomer.phone || null,
            industry: newCustomer.industry || null,
            notes: newCustomer.location ? `Location: ${newCustomer.location}` : null,
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success) saved = json.data;
      } catch (e) { console.warn('[addCustomer] DB save failed:', e.message); }

      const customer = {
        id: saved ? String(saved.id) : `cust_${Date.now()}`,
        ...newCustomer,
        registrationDate: new Date().toISOString().split('T')[0],
      };

      const updatedCustomers = [customer, ...customers];
      setCustomers(updatedCustomers);
      try { await AsyncStorage.setItem('epr_customers_' + companyId, JSON.stringify(updatedCustomers)); } catch {}
      
      setShowCustomerModal(false);
      setNewCustomer({ name: '', company: '', email: '', phone: '', location: '', industry: '' });
      
      Alert.alert('Success', 'Customer added successfully');
    } catch (error) {
      console.error('Error adding customer:', error);
      Alert.alert('Error', 'Failed to add customer');
    }
  };

  const addOpportunity = async () => {
    try {
      if (!newOpportunity.name?.trim()) {
        Alert.alert('Required', 'Please enter an opportunity name');
        return;
      }
      const companyId = await jeCidNum();
      const headers = await jeHeaders();
      const cust = customers.find(c => c.id === newOpportunity.customerId);

      let saved = null;
      try {
        const res = await fetch(`${JE_API_BASE}/api/epr/crm/deals`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            company_id: companyId,
            company: cust?.company || cust?.name || newOpportunity.name,
            contact_name: cust?.name || null,
            contact_email: cust?.email || null,
            stage: newOpportunity.stage || 'lead',
            value: parseFloat(String(newOpportunity.value).replace(/[^0-9.]/g, '')) || 0,
            probability: parseInt(newOpportunity.probability, 10) || 30,
            notes: newOpportunity.name,
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success) saved = json.data;
      } catch (e) { console.warn('[addOpportunity] DB save failed:', e.message); }

      const opportunity = {
        id: saved ? String(saved.id) : `opp_${Date.now()}`,
        ...newOpportunity,
        createdAt: new Date().toISOString().split('T')[0],
        customerName: cust?.name || 'Unknown',
        company: cust?.company || 'Unknown',
      };

      const updatedOpportunities = [opportunity, ...opportunities];
      setOpportunities(updatedOpportunities);
      setSelectedOpportunity(opportunity);
      try { await AsyncStorage.setItem('epr_opportunities_' + companyId, JSON.stringify(updatedOpportunities)); } catch {}
      
      setShowOpportunityModal(false);
      setNewOpportunity({ name: '', customerId: '', stage: 'prospecting', value: '', probability: 50, expectedCloseDate: new Date() });
      
      Alert.alert('Success', 'Opportunity added successfully');
    } catch (error) {
      console.error('Error adding opportunity:', error);
      Alert.alert('Error', 'Failed to add opportunity');
    }
  };

  // Log a customer interaction → crm_activities
  const addInteraction = async () => {
    try {
      if (!newInteraction.title?.trim()) {
        Alert.alert('Required', 'Please enter an interaction title');
        return;
      }
      const companyId = await jeCidNum();
      const headers = await jeHeaders();

      let saved = null;
      try {
        const res = await fetch(`${JE_API_BASE}/api/epr/crm/activities`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            company_id: companyId,
            type: newInteraction.type || 'call',
            title: newInteraction.title,
            description: newInteraction.description || null,
            duration: parseInt(newInteraction.duration, 10) || 30,
            notes: newInteraction.notes || null,
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success) saved = json.data;
      } catch (e) { console.warn('[addInteraction] DB save failed:', e.message); }

      const interaction = {
        id: saved ? String(saved.id) : `int_${Date.now()}`,
        ...newInteraction,
        date: new Date().toISOString(),
      };
      const updated = [interaction, ...customerInteractions];
      setCustomerInteractions(updated);
      try { await AsyncStorage.setItem('epr_interactions_' + companyId, JSON.stringify(updated)); } catch {}

      setShowInteractionModal(false);
      setNewInteraction({ type: 'call', title: '', description: '', duration: '30', notes: '' });
      Alert.alert('Success', 'Interaction logged successfully');
    } catch (error) {
      console.error('Error logging interaction:', error);
      Alert.alert('Error', 'Failed to log interaction');
    }
  };

  // Export Data
  const exportData = async (format) => {
    try {
      const data = {
        products: products,
        customers: customers,
        opportunities: opportunities,
        dashboard: eprData
      };

      const dataStr = JSON.stringify(data, null, 2);
      Alert.alert('Export Successful', `Data prepared in ${format.toUpperCase()} format`);
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      Alert.alert('Export Failed', 'Could not export data');
    }
  };

  // Helper Functions
  const getTrendIcon = useCallback((trend) => {
    switch (trend) {
      case 'up':
        return 'trending-up';
      case 'down':
        return 'trending-down';
      default:
        return 'trending-neutral';
    }
  }, []);

  const getTrendColor = useCallback((trend) => {
    switch (trend) {
      case 'up':
        return '#10B981';
      case 'down':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  }, []);

  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  }, []);

  const getComplianceColor = useCallback((compliance) => {
    switch (compliance) {
      case 'compliant':
        return '#10B981';
      case 'pending':
        return '#F59E0B';
      case 'non_compliant':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  }, []);

  // Handle refresh
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboardData(),
        loadProducts(),
        loadCustomers(),
        loadOpportunities()
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [loadDashboardData, loadProducts, loadCustomers, loadOpportunities]);

  // Header Component
  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerTop}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#1F2937" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>EPR Management System</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.notificationButton}
            onPress={() => setShowNotificationsModal(true)}
          >
            <MaterialCommunityIcons name="bell" size={24} color="#6B7280" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickActionButton}
            onPress={() => setShowQuickActionModal(true)}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#8B5CF6" />
          </TouchableOpacity>
        </View>
      </View>

      <LinearGradient
        colors={['#10B981', '#059669']}
        style={styles.welcomeCard}
      >
        <Text style={styles.welcomeTitle}>Environmental Compliance Dashboard</Text>
        <Text style={styles.welcomeSubtitle}>
          Manage EPR obligations, track compliance, and drive sustainability
        </Text>
      </LinearGradient>
    </View>
  );

  // Overview Cards
  const renderOverviewCards = () => (
    <View style={styles.overviewSection}>
      <Text style={styles.sectionTitle}>EPR Overview</Text>
      <View style={styles.overviewGrid}>
        <View style={styles.overviewCard}>
          <MaterialCommunityIcons name="package-variant" size={24} color="#3B82F6" />
          <Text style={styles.overviewValue}>{eprData.overview?.totalProducts || 0}</Text>
          <Text style={styles.overviewLabel}>Total Products</Text>
        </View>
        <View style={styles.overviewCard}>
          <MaterialCommunityIcons name="shield-check" size={24} color="#10B981" />
          <Text style={styles.overviewValue}>{eprData.overview?.complianceScore || 0}%</Text>
          <Text style={styles.overviewLabel}>Compliance Score</Text>
        </View>
        <View style={styles.overviewCard}>
          <MaterialCommunityIcons name="briefcase" size={24} color="#F59E0B" />
          <Text style={styles.overviewValue}>{eprData.overview?.activeOpportunities || 0}</Text>
          <Text style={styles.overviewLabel}>Opportunities</Text>
        </View>
        <View style={styles.overviewCard}>
          <MaterialCommunityIcons name="account-group" size={24} color="#8B5CF6" />
          <Text style={styles.overviewValue}>{eprData.overview?.totalCustomers || 0}</Text>
          <Text style={styles.overviewLabel}>Customers</Text>
        </View>
      </View>
    </View>
  );

  // EPR Categories
  const renderEPRCategories = () => (
    <View style={styles.modulesSection}>
      <Text style={styles.sectionTitle}>EPR Categories</Text>
      <View style={styles.modulesGrid}>
        {eprCategories.map((category) => (
          <TouchableOpacity
            key={category.id}
            style={[styles.moduleCard, { borderLeftColor: category.color }]}
            onPress={() => {
              // Navigate to category details
              Alert.alert(category.name, category.description);
            }}
          >
            <View style={styles.moduleHeader}>
              <View style={[styles.moduleIcon, { backgroundColor: `${category.color}20` }]}>
                <MaterialCommunityIcons name={category.icon} size={24} color={category.color} />
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
            </View>
            <Text style={styles.moduleName}>{category.name}</Text>
            <Text style={styles.moduleDescription} numberOfLines={2}>
              {category.description}
            </Text>
            
            {category.kpis?.[0] && (
              <View style={styles.moduleKPI}>
                <Text style={styles.kpiLabel}>{category.kpis[0].label}</Text>
                <View style={styles.kpiValue}>
                  <Text style={styles.kpiNumber}>{category.kpis[0].value}</Text>
                  <View style={styles.kpiTrend}>
                    <MaterialCommunityIcons
                      name={getTrendIcon(category.kpis[0].trend)}
                      size={12}
                      color={getTrendColor(category.kpis[0].trend)}
                    />
                    <Text style={[styles.kpiChange, { color: getTrendColor(category.kpis[0].trend) }]}>
                      {category.kpis[0].change}
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // Quick Actions
  const renderQuickActions = () => (
    <View style={styles.quickActionsSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <TouchableOpacity onPress={() => setShowQuickActionModal(true)}>
          <Text style={styles.viewAllText}>View All</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.quickActionsContainer}
      >
        {quickActions.slice(0, 4).map((action) => (
          <TouchableOpacity
            key={action.id}
            style={styles.quickActionItem}
            onPress={action.action}
          >
            <View style={[styles.quickActionIconContainer, { backgroundColor: `${action.color}20` }]}>
              <MaterialCommunityIcons name={action.icon} size={20} color={action.color} />
            </View>
            <Text style={styles.quickActionText}>{action.title}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Recent Activities
  const renderRecentActivities = () => (
    <View style={styles.activitiesSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Recent Activities</Text>
        <TouchableOpacity onPress={() => setShowAllActivitiesModal(true)}>
          <Text style={styles.viewAllText}>View All</Text>
        </TouchableOpacity>
      </View>
      {(eprData.recentActivities || []).map((activity) => (
        <View key={activity.id} style={styles.activityItem}>
          <View style={[styles.activityIcon, { backgroundColor: `${activity.color}20` }]}>
            <MaterialCommunityIcons name={activity.icon} size={20} color={activity.color} />
          </View>
          <View style={styles.activityContent}>
            <Text style={styles.activityTitle}>{activity.title}</Text>
            <Text style={styles.activityDescription}>{activity.description}</Text>
            <Text style={styles.activityTimestamp}>{activity.timestamp}</Text>
          </View>
        </View>
      ))}
    </View>
  );

  // Upcoming Deadlines
  const renderUpcomingDeadlines = () => (
    <View style={styles.tasksSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Upcoming Deadlines</Text>
        <TouchableOpacity onPress={() => setShowAllDeadlinesModal(true)}>
          <Text style={styles.viewAllText}>View All</Text>
        </TouchableOpacity>
      </View>
      {(eprData.upcomingDeadlines || []).map((deadline) => {
        const daysUntilDue = Math.ceil((new Date(deadline.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
        return (
          <View key={deadline.id} style={styles.taskItem}>
            <View style={styles.taskInfo}>
              <Text style={styles.taskTitle}>{deadline.title}</Text>
              <Text style={styles.taskCategory}>{deadline.category}</Text>
              <Text style={[
                styles.taskDueDate,
                { color: daysUntilDue <= 7 ? '#EF4444' : daysUntilDue <= 30 ? '#F59E0B' : '#10B981' }
              ]}>
                Due: {deadline.dueDate} ({daysUntilDue <= 0 ? 'OVERDUE' : `${daysUntilDue} days`})
              </Text>
            </View>
            <View style={[styles.taskPriority, { backgroundColor: getPriorityColor(deadline.priority) }]}>
              <Text style={styles.taskPriorityText}>{deadline.priority}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );

  // Environmental Metrics
  const renderEnvironmentalMetrics = () => (
    <View style={styles.metricsSection}>
      <Text style={styles.sectionTitle}>Environmental Metrics</Text>
      <View style={styles.metricsGrid}>
        {(eprData.environmentalMetrics || []).map((metric) => (
          <View key={metric.id} style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Text style={styles.metricLabel}>{metric.label}</Text>
              <MaterialCommunityIcons
                name={getTrendIcon(metric.trend)}
                size={16}
                color={getTrendColor(metric.trend)}
              />
            </View>
            <Text style={styles.metricValue}>{metric.value}</Text>
            <Text style={styles.metricUnit}>{metric.unit}</Text>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${metric.progress}%`,
                    backgroundColor: metric.trend === 'up' ? '#10B981' : '#EF4444'
                  }
                ]}
              />
            </View>
            <Text style={[styles.changeText, { color: getTrendColor(metric.trend) }]}>
              {metric.change > 0 ? '+' : ''}{metric.change}%
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  // Product List
  const renderProductList = () => (
    <View style={styles.productsSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Products</Text>
        <TouchableOpacity onPress={() => setShowProductModal(true)}>
          <Text style={styles.viewAllText}>Add Product</Text>
        </TouchableOpacity>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {products.slice(0, 3).map((product) => (
          <View key={product.id} style={styles.productCard}>
            <View style={styles.productHeader}>
              <MaterialCommunityIcons
                name={eprCategories.find(c => c.id === product.category)?.icon || 'package-variant'}
                size={20}
                color={eprCategories.find(c => c.id === product.category)?.color || '#6B7280'}
              />
              <Text style={styles.productName} numberOfLines={1}>{product.name}</Text>
            </View>
            <View style={styles.productDetails}>
              <View style={styles.productDetail}>
                <Text style={styles.detailLabel}>Category:</Text>
                <Text style={styles.detailValue}>{product.category}</Text>
              </View>
              <View style={styles.productDetail}>
                <Text style={styles.detailLabel}>Recyclability:</Text>
                <Text style={styles.detailValue}>{product.recyclability}</Text>
              </View>
              <View style={styles.productDetail}>
                <Text style={styles.detailLabel}>EPR Fee:</Text>
                <Text style={styles.detailValue}>{product.eprFee}</Text>
              </View>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: `${getComplianceColor(product.status)}20` }]}>
              <Text style={[styles.statusText, { color: getComplianceColor(product.status) }]}>
                {String(product.status ?? '').toUpperCase()}
              </Text>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  // Active Opportunity
  const renderActiveOpportunity = () => (
    selectedOpportunity && (
      <View style={styles.opportunitySection}>
        <Text style={styles.sectionTitle}>Active Opportunity</Text>
        <View style={styles.opportunityCard}>
          <View style={styles.opportunityHeader}>
            <Text style={styles.opportunityTitle}>{selectedOpportunity.name}</Text>
            <View style={styles.probabilityBadge}>
              <Text style={styles.probabilityText}>{selectedOpportunity.probability}%</Text>
            </View>
          </View>
          <Text style={styles.opportunityCompany}>{selectedOpportunity.company}</Text>
          <Text style={styles.opportunityContact}>{selectedOpportunity.customerName}</Text>
          <View style={styles.opportunityDetails}>
            <Text style={styles.opportunityValue}>Value: £{selectedOpportunity.value?.toLocaleString()}</Text>
            <Text style={styles.opportunityStage}>Stage: {selectedOpportunity.stage}</Text>
          </View>
          <TouchableOpacity
            style={styles.viewDetailsButton}
            onPress={() => setShowOpportunityModal(true)}
          >
            <Text style={styles.viewDetailsText}>View Details</Text>
          </TouchableOpacity>
        </View>
      </View>
    )
  );

  // Create unified data for FlatList
  const flatListData = useMemo(() => {
    const data = [];
    
    data.push({ id: 'overview', type: 'overview' });
    data.push({ id: 'quick-actions', type: 'quick-actions' });
    data.push({ id: 'categories', type: 'categories' });
    data.push({ id: 'products', type: 'products' });
    data.push({ id: 'opportunity', type: 'opportunity' });
    data.push({ id: 'metrics', type: 'metrics' });
    data.push({ id: 'activities', type: 'activities' });
    data.push({ id: 'deadlines', type: 'deadlines' });
    
    return data;
  }, []);

  // Unified render function
  const renderUnifiedItem = useCallback(({ item }) => {
    switch (item.type) {
      case 'overview':
        return renderOverviewCards();
      case 'quick-actions':
        return renderQuickActions();
      case 'categories':
        return renderEPRCategories();
      case 'products':
        return renderProductList();
      case 'opportunity':
        return renderActiveOpportunity();
      case 'metrics':
        return renderEnvironmentalMetrics();
      case 'activities':
        return renderRecentActivities();
      case 'deadlines':
        return renderUpcomingDeadlines();
      default:
        return null;
    }
  }, [
    renderOverviewCards,
    renderQuickActions,
    renderEPRCategories,
    renderProductList,
    renderActiveOpportunity,
    renderEnvironmentalMetrics,
    renderRecentActivities,
    renderUpcomingDeadlines
  ]);

  // Modal Components
  const renderNotificationsModal = () => (
    <Modal
      visible={showNotificationsModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowNotificationsModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowNotificationsModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Notifications</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.modalContent}>
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="bell-off" size={48} color="#9CA3AF" />
            <Text style={styles.emptyStateText}>No notifications</Text>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderQuickActionsModal = () => (
    <Modal
      visible={showQuickActionModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowQuickActionModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowQuickActionModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Quick Actions</Text>
          <View style={{ width: 24 }} />
        </View>
        <ScrollView style={styles.modalContent}>
          <View style={styles.quickActionsGrid}>
            {quickActions.map((action) => (
              <TouchableOpacity
                key={action.id}
                style={styles.quickActionCard}
                onPress={() => {
                  setShowQuickActionModal(false);
                  action.action();
                }}
              >
                <View style={[styles.quickActionIcon, { backgroundColor: `${action.color}20` }]}>
                  <MaterialCommunityIcons name={action.icon} size={24} color={action.color} />
                </View>
                <Text style={styles.quickActionTitle}>{action.title}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderExportModal = () => (
    <Modal
      visible={showExportModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowExportModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Export Data</Text>
            <TouchableOpacity onPress={() => setShowExportModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <View style={styles.modalBody}>
            <Text style={styles.modalDescription}>Select export format:</Text>
            
            <TouchableOpacity
              style={styles.exportOption}
              onPress={() => exportData('json')}
            >
              <MaterialCommunityIcons name="code-json" size={24} color="#3B82F6" />
              <View style={styles.exportOptionInfo}>
                <Text style={styles.exportOptionTitle}>JSON Format</Text>
                <Text style={styles.exportOptionDescription}>Complete data export</Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.exportOption}
              onPress={() => exportData('csv')}
            >
              <MaterialCommunityIcons name="file-table" size={24} color="#10B981" />
              <View style={styles.exportOptionInfo}>
                <Text style={styles.exportOptionTitle}>CSV Format</Text>
                <Text style={styles.exportOptionDescription}>Spreadsheet ready data</Text>
              </View>
            </TouchableOpacity>
          </View>
          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => setShowExportModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderProductModal = () => (
    <Modal
      visible={showProductModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowProductModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Product</Text>
            <TouchableOpacity onPress={() => setShowProductModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Product Name</Text>
              <TextInput
                style={styles.input}
                value={newProduct.name}
                onChangeText={(text) => setNewProduct({...newProduct, name: text})}
                placeholder="Enter product name"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Category</Text>
              <View style={styles.categoryContainer}>
                {eprCategories.map((cat) => (
                  <TouchableOpacity
                    key={cat.id}
                    style={[
                      styles.categoryOption,
                      newProduct.category === cat.id && { backgroundColor: cat.color }
                    ]}
                    onPress={() => setNewProduct({...newProduct, category: cat.id})}
                  >
                    <MaterialCommunityIcons
                      name={cat.icon}
                      size={16}
                      color={newProduct.category === cat.id ? '#FFFFFF' : cat.color}
                    />
                    <Text style={[
                      styles.categoryText,
                      newProduct.category === cat.id && { color: '#FFFFFF' }
                    ]}>
                      {cat.name.split(' ')[0]}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={styles.formGroupHalf}>
                <Text style={styles.label}>Weight (kg)</Text>
                <TextInput
                  style={styles.input}
                  value={newProduct.weight}
                  onChangeText={(text) => setNewProduct({...newProduct, weight: text})}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.formGroupHalf}>
                <Text style={styles.label}>Recyclability (%)</Text>
                <TextInput
                  style={styles.input}
                  value={newProduct.recyclability}
                  onChangeText={(text) => setNewProduct({...newProduct, recyclability: text})}
                  placeholder="0-100"
                  keyboardType="numeric"
                />
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Carbon Footprint</Text>
              <TextInput
                style={styles.input}
                value={newProduct.carbonFootprint}
                onChangeText={(text) => setNewProduct({...newProduct, carbonFootprint: text})}
                placeholder="kg CO₂e"
                keyboardType="decimal-pad"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>EPR Fee (£)</Text>
              <TextInput
                style={styles.input}
                value={newProduct.eprFee}
                onChangeText={(text) => setNewProduct({...newProduct, eprFee: text})}
                placeholder="0.00"
                keyboardType="decimal-pad"
              />
            </View>
          </ScrollView>
          <View style={styles.modalFooter}>
            <View style={styles.modalButtonGroup}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowProductModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={addProduct}
                disabled={!newProduct.name}
              >
                <Text style={styles.saveButtonText}>Add Product</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderCustomerModal = () => (
    <Modal
      visible={showCustomerModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowCustomerModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Customer</Text>
            <TouchableOpacity onPress={() => setShowCustomerModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Customer Name *</Text>
              <TextInput style={styles.input} value={newCustomer.name}
                onChangeText={v => setNewCustomer(p => ({ ...p, name: v }))}
                placeholder="Customer name" placeholderTextColor="#94a3b8" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Company</Text>
              <TextInput style={styles.input} value={newCustomer.company}
                onChangeText={v => setNewCustomer(p => ({ ...p, company: v }))}
                placeholder="Company name" placeholderTextColor="#94a3b8" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput style={styles.input} value={newCustomer.email}
                onChangeText={v => setNewCustomer(p => ({ ...p, email: v }))}
                placeholder="email@example.com" placeholderTextColor="#94a3b8"
                keyboardType="email-address" autoCapitalize="none" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Phone</Text>
              <TextInput style={styles.input} value={newCustomer.phone}
                onChangeText={v => setNewCustomer(p => ({ ...p, phone: v }))}
                placeholder="+44 ..." placeholderTextColor="#94a3b8" keyboardType="phone-pad" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Industry</Text>
              <TextInput style={styles.input} value={newCustomer.industry}
                onChangeText={v => setNewCustomer(p => ({ ...p, industry: v }))}
                placeholder="Industry" placeholderTextColor="#94a3b8" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Location</Text>
              <TextInput style={styles.input} value={newCustomer.location}
                onChangeText={v => setNewCustomer(p => ({ ...p, location: v }))}
                placeholder="City, Country" placeholderTextColor="#94a3b8" />
            </View>
            <TouchableOpacity style={[styles.modalButton, styles.saveButton, { margin: 0, marginTop: 8 }]} onPress={addCustomer}>
              <Text style={styles.saveButtonText}>Add Customer</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderOpportunityModal = () => (
    <Modal
      visible={showOpportunityModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowOpportunityModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Opportunity</Text>
            <TouchableOpacity onPress={() => setShowOpportunityModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Opportunity Name *</Text>
              <TextInput style={styles.input} value={newOpportunity.name}
                onChangeText={v => setNewOpportunity(p => ({ ...p, name: v }))}
                placeholder="e.g. EPR compliance package" placeholderTextColor="#94a3b8" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Stage</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {['lead', 'prospecting', 'proposal', 'negotiation', 'won'].map(s => (
                  <TouchableOpacity key={s}
                    onPress={() => setNewOpportunity(p => ({ ...p, stage: s }))}
                    style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 20,
                      backgroundColor: newOpportunity.stage === s ? '#0e4e96' : '#F1F5F9' }}>
                    <Text style={{ color: newOpportunity.stage === s ? '#fff' : '#475569',
                      fontWeight: '600', textTransform: 'capitalize' }}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Value (£)</Text>
              <TextInput style={styles.input} value={String(newOpportunity.value)}
                onChangeText={v => setNewOpportunity(p => ({ ...p, value: v }))}
                placeholder="50000" placeholderTextColor="#94a3b8" keyboardType="numeric" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Probability: {newOpportunity.probability}%</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {[25, 50, 75, 90].map(p => (
                  <TouchableOpacity key={p}
                    onPress={() => setNewOpportunity(prev => ({ ...prev, probability: p }))}
                    style={{ flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center',
                      backgroundColor: newOpportunity.probability === p ? '#0e4e96' : '#F1F5F9' }}>
                    <Text style={{ color: newOpportunity.probability === p ? '#fff' : '#475569', fontWeight: '700' }}>{p}%</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <TouchableOpacity style={[styles.modalButton, styles.saveButton, { margin: 0, marginTop: 8 }]} onPress={addOpportunity}>
              <Text style={styles.saveButtonText}>Add Opportunity</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderInteractionModal = () => (
    <Modal
      visible={showInteractionModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowInteractionModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Log Interaction</Text>
            <TouchableOpacity onPress={() => setShowInteractionModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Type</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {['call', 'email', 'meeting', 'note', 'task'].map(t => (
                  <TouchableOpacity key={t}
                    onPress={() => setNewInteraction(p => ({ ...p, type: t }))}
                    style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 20,
                      backgroundColor: newInteraction.type === t ? '#3B82F6' : '#F1F5F9' }}>
                    <Text style={{ color: newInteraction.type === t ? '#fff' : '#475569',
                      fontWeight: '600', textTransform: 'capitalize' }}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Title *</Text>
              <TextInput style={styles.input} value={newInteraction.title}
                onChangeText={v => setNewInteraction(p => ({ ...p, title: v }))}
                placeholder="e.g. Follow-up call with client" placeholderTextColor="#94a3b8" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Description</Text>
              <TextInput style={[styles.input, { height: 80, textAlignVertical: 'top' }]}
                value={newInteraction.description}
                onChangeText={v => setNewInteraction(p => ({ ...p, description: v }))}
                placeholder="What was discussed..." placeholderTextColor="#94a3b8"
                multiline numberOfLines={3} />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Duration (minutes)</Text>
              <TextInput style={styles.input} value={String(newInteraction.duration)}
                onChangeText={v => setNewInteraction(p => ({ ...p, duration: v }))}
                placeholder="30" placeholderTextColor="#94a3b8" keyboardType="numeric" />
            </View>
            <TouchableOpacity style={[styles.modalButton, styles.saveButton, { margin: 0, marginTop: 8 }]} onPress={addInteraction}>
              <Text style={styles.saveButtonText}>Log Interaction</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderAllActivitiesModal = () => (
    <Modal
      visible={showAllActivitiesModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowAllActivitiesModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>All Activities</Text>
            <TouchableOpacity onPress={() => setShowAllActivitiesModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            {(eprData.recentActivities || []).length === 0 ? (
              <View style={{ alignItems: 'center', padding: 40 }}>
                <MaterialCommunityIcons name="history" size={48} color="#9CA3AF" />
                <Text style={{ color: '#6B7280', marginTop: 12 }}>No activities yet</Text>
              </View>
            ) : (
              (eprData.recentActivities || []).map((activity) => (
                <View key={activity.id} style={styles.activityItem}>
                  <View style={[styles.activityIcon, { backgroundColor: `${activity.color || '#0e4e96'}20` }]}>
                    <MaterialCommunityIcons name={activity.icon || 'circle'} size={20} color={activity.color || '#0e4e96'} />
                  </View>
                  <View style={styles.activityContent}>
                    <Text style={styles.activityTitle}>{activity.title}</Text>
                    <Text style={styles.activityDescription}>{activity.description}</Text>
                    <Text style={styles.activityTimestamp}>{activity.timestamp}</Text>
                  </View>
                </View>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderAllDeadlinesModal = () => (
    <Modal
      visible={showAllDeadlinesModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowAllDeadlinesModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContentContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>All Deadlines</Text>
            <TouchableOpacity onPress={() => setShowAllDeadlinesModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.modalBody}>
            {(eprData.upcomingDeadlines || []).length === 0 ? (
              <View style={{ alignItems: 'center', padding: 40 }}>
                <MaterialCommunityIcons name="calendar-check" size={48} color="#9CA3AF" />
                <Text style={{ color: '#6B7280', marginTop: 12 }}>No upcoming deadlines</Text>
              </View>
            ) : (
              (eprData.upcomingDeadlines || []).map((deadline, idx) => {
                const daysUntilDue = Math.ceil((new Date(deadline.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                const urgent = daysUntilDue <= 7;
                return (
                  <View key={deadline.id || idx} style={[styles.activityItem, { borderLeftWidth: 3, borderLeftColor: urgent ? '#EF4444' : '#F59E0B' }]}>
                    <View style={[styles.activityIcon, { backgroundColor: urgent ? '#FEE2E2' : '#FEF3C7' }]}>
                      <MaterialCommunityIcons name="calendar-clock" size={20} color={urgent ? '#EF4444' : '#F59E0B'} />
                    </View>
                    <View style={styles.activityContent}>
                      <Text style={styles.activityTitle}>{deadline.title}</Text>
                      <Text style={styles.activityDescription}>{deadline.description || ''}</Text>
                      <Text style={[styles.activityTimestamp, { color: urgent ? '#EF4444' : '#F59E0B', fontWeight: '700' }]}>
                        {isNaN(daysUntilDue) ? deadline.dueDate : daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : `${daysUntilDue} days left`}
                      </Text>
                    </View>
                  </View>
                );
              })
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderAIModal = () => (
    <Modal
      visible={showAIModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowAIModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowAIModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>AI Assistant</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.modalContent}>
          <ScrollView style={styles.messagesContainer}>
            {aiMessages.map((message) => (
              <View
                key={message.id}
                style={[
                  styles.messageBubble,
                  message.type === 'user' ? styles.userMessage : styles.aiMessage
                ]}
              >
                <Text style={styles.messageText}>{message.content}</Text>
              </View>
            ))}
            {isAiTyping && (
              <View style={[styles.messageBubble, styles.aiMessage]}>
                <Text style={styles.typingText}>AI is thinking...</Text>
              </View>
            )}
          </ScrollView>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.aiInput}
              placeholder="Ask about EPR compliance..."
              value={aiInput}
              onChangeText={setAiInput}
              multiline
            />
            <TouchableOpacity
              style={styles.sendButton}
              onPress={handleSendToAI}
              disabled={!aiInput.trim()}
            >
              <MaterialCommunityIcons name="send" size={20} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        
      {/* AI Assistant FAB */}
      <TouchableOpacity 
        onPress={() => setShowAIChat(true)} 
        style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="EPRScreen" visible={showAIChat} onClose={() => setShowAIChat(false)} title="EPR AI" extraContext={{}} />

    </View>
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      {renderHeader()}
      
      <FlatList
        ref={flatListRef}
        data={flatListData}
        renderItem={renderUnifiedItem}
        keyExtractor={(item) => item.id}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.contentContainer}
        onScrollToIndexFailed={(info) => {
          // Fallback: scroll to offset if exact index measurement isn't ready
          setTimeout(() => {
            flatListRef.current?.scrollToIndex({ index: info.index, animated: true });
          }, 300);
        }}
      />

      {/* Modals */}
      {renderNotificationsModal()}
      {renderQuickActionsModal()}
      {renderExportModal()}
      {renderProductModal()}
      {renderCustomerModal()}
      {renderOpportunityModal()}
      {renderInteractionModal()}
      {renderAllActivitiesModal()}
      {renderAllDeadlinesModal()}
      {renderAIModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  contentContainer: {
    paddingBottom: 20,
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  notificationButton: {
    padding: 8,
    marginRight: 8,
  },
  quickActionButton: {
    padding: 8,
  },
  welcomeCard: {
    borderRadius: 12,
    padding: 20,
    marginTop: 8,
  },
  welcomeTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  welcomeSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  overviewSection: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  overviewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  overviewCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '48%',
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  overviewValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
  },
  overviewLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  modulesSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  modulesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  moduleCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '48%',
    marginBottom: 12,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  moduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  moduleIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moduleName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  moduleDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
    marginBottom: 12,
  },
  moduleKPI: {
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 8,
  },
  kpiLabel: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 4,
  },
  kpiValue: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  kpiNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  kpiTrend: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  kpiChange: {
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 2,
  },
  quickActionsSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  viewAllText: {
    fontSize: 14,
    color: '#10B981',
    fontWeight: '500',
  },
  quickActionsContainer: {
    paddingRight: 20,
  },
  quickActionItem: {
    alignItems: 'center',
    marginRight: 16,
    width: 80,
  },
  quickActionIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  quickActionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#1F2937',
    textAlign: 'center',
  },
  activitiesSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  activityDescription: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 4,
  },
  activityTimestamp: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  tasksSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  taskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  taskInfo: {
    flex: 1,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  taskCategory: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  taskDueDate: {
    fontSize: 12,
    fontWeight: '500',
  },
  taskPriority: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  taskPriorityText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#FFFFFF',
    textTransform: 'capitalize',
  },
  metricsSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '48%',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
    flex: 1,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 2,
  },
  metricUnit: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 8,
  },
  progressBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  changeText: {
    fontSize: 11,
    fontWeight: '500',
  },
  productsSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  productCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: 200,
    marginRight: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  productHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  productName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginLeft: 8,
    flex: 1,
  },
  productDetails: {
    marginBottom: 12,
  },
  productDetail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailLabel: {
    fontSize: 11,
    color: '#6B7280',
  },
  detailValue: {
    fontSize: 11,
    fontWeight: '500',
    color: '#1F2937',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '500',
  },
  opportunitySection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  opportunityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  opportunityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  opportunityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  probabilityBadge: {
    backgroundColor: '#10B981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  probabilityText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  opportunityCompany: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '500',
    marginBottom: 4,
  },
  opportunityContact: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 12,
  },
  opportunityDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  opportunityValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
  },
  opportunityStage: {
    fontSize: 14,
    color: '#6B7280',
  },
  viewDetailsButton: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#3B82F6',
  },
  // Modal Styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#9CA3AF',
    marginTop: 16,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickActionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: '48%',
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  quickActionTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#1F2937',
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContentContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxHeight: '80%',
  },
  modalBody: {
    padding: 20,
  },
  modalDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 20,
  },
  modalFooter: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  modalButtonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modalButton: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    marginRight: 8,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  saveButton: {
    backgroundColor: '#10B981',
    marginLeft: 8,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  exportOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  exportOptionInfo: {
    flex: 1,
    marginLeft: 12,
  },
  exportOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  exportOptionDescription: {
    fontSize: 12,
    color: '#6B7280',
  },
  // Form Styles
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1F2937',
  },
  formRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  formGroupHalf: {
    width: '48%',
  },
  categoryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    gap: 6,
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
  },
  // AI Modal Styles
  messagesContainer: {
    flex: 1,
    marginBottom: 16,
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 12,
    borderRadius: 16,
    marginBottom: 8,
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#3B82F6',
  },
  aiMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#F3F4F6',
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#1F2937',
  },
  userMessageText: {
    color: '#FFFFFF',
  },
  typingText: {
    fontSize: 14,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 16,
  },
  aiInput: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1F2937',
    maxHeight: 100,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#8B5CF6',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
});

export default EnvironmentalProductResponsibilityScreen;